import { Link, Outlet, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  FolderOpen,
  Clock,
  TrendingUp,
  Eye,
  PiggyBank,
  Building2,
  BarChart3,
  ExternalLink,
  User,
  Bell,
  FileDown,
  Settings,
  HelpCircle,
  LogOut,
  Search,
} from "lucide-react";
const NpsLogo = "/assets/NPSLogo.png";
import {
  RegistrationDetailsProvider,
  useRegistrationDetails,
} from "../../context/registrationDetails.tsx";

// User Info Component that uses the context
const UserInfo = () => {
  const { registrationDetails } = useRegistrationDetails();

  const firstName = registrationDetails?.personalDtls?.subFstName ?? "";

  // Get initials from first name - first letter of first word and first letter of second word
  const getInitials = (name: string) => {
    if (!name) {
      return "U";
    }
    const words = name
      .trim()
      .split(" ")
      .filter(word => word.length > 0);
    if (words.length >= 2) {
      // Take first letter of first word and first letter of second word
      return (words[0][0] + words[1][0]).toUpperCase();
    } else if (words.length === 1) {
      // If only one word, take first 2 letters
      return words[0].substring(0, 2).toUpperCase();
    }
    return "U";
  };

  const initials = getInitials(firstName);

  return (
    <div className="flex items-center gap-3">
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-teal-500 font-semibold text-white">
        <span>{initials}</span>
      </div>
      <div className="hidden sm:block">
        <div className="text-sm font-semibold text-gray-800">
          {firstName || "User"}
        </div>
        {/* <div className="text-xs text-gray-500">Premium User</div> */}
      </div>
    </div>
  );
};

const NpsUserLayout = () => {
  const location = useLocation();

  const isActive = (path: string) => {
    return (
      location.pathname === path || location.pathname.startsWith(path + "/")
    );
  };

  const dashboardItems = [
    {
      icon: LayoutDashboard,
      label: "Overview",
      path: "/nps-user/dashboard/overview",
    },
    {
      icon: FolderOpen,
      label: "My Portfolio",
      path: "/nps-user/dashboard/portfolio",
    },
    {
      icon: Clock,
      label: "Transactions",
      path: "/nps-user/dashboard/transactions",
    },
  ];

  const productItems = [
    {
      icon: TrendingUp,
      label: "IPO",
      path: "/nps-user/products/ipo",
    },
    {
      icon: Eye,
      label: "Bonds",
      path: "/nps-user/products/bonds",
    },
    {
      icon: PiggyBank,
      label: "Fixed Deposits",
      path: "/nps-user/products/fixed-deposits",
    },
    {
      icon: Building2,
      label: "NPS",
      path: "/nps-user/nps-overview",
    },
    {
      icon: BarChart3,
      label: "Screener",
      path: "/nps-user/products/screener",
      external: true,
    },
  ];

  const accountItems = [
    {
      icon: User,
      label: "Profile & KYC",
      path: "/nps-user/account/profile",
    },
    {
      icon: Bell,
      label: "Notifications",
      path: "/nps-user/account/notifications",
    },
    {
      icon: FileDown,
      label: "Reports",
      path: "/nps-user/account/reports",
    },
    {
      icon: Settings,
      label: "Settings",
      path: "/nps-user/account/settings",
    },
  ];

  return (
    <RegistrationDetailsProvider>
      <div className="flex h-screen bg-gray-50">
        {/* Sidebar */}
        <aside className="flex w-64 flex-col border-r border-gray-200 bg-white">
          {/* Logo Section */}
          <div className="border-b border-gray-200 p-4">
            <Link to="/nps-user/dashboard" className="flex items-center gap-2">
              <img
                src={NpsLogo}
                alt="logo"
                className="h-10 w-auto max-w-full"
              />
            </Link>
          </div>

          {/* Navigation Menu */}
          <nav className="flex-1 overflow-y-auto py-4">
            {/* DASHBOARD Section */}
            <div className="mb-6 px-2">
              <div className="mb-2 px-3">
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  DASHBOARD
                </span>
              </div>
              <div className="space-y-1">
                {dashboardItems.map(item => {
                  const Icon = item.icon;
                  const active = isActive(item.path);
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center gap-3 rounded-lg px-3 py-2.5 transition-colors ${
                        active
                          ? "bg-gray-100 text-teal-600"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      <Icon className="h-5 w-5" />
                      <span className="text-sm font-medium">{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            </div>

            {/* PRODUCTS Section */}
            <div className="mb-6 px-2">
              <div className="mb-2 px-3">
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  PRODUCTS
                </span>
              </div>
              <div className="space-y-1">
                {productItems.map(item => {
                  const Icon = item.icon;
                  const active = isActive(item.path);
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center justify-between rounded-lg px-3 py-2.5 transition-colors ${
                        active
                          ? "bg-gray-100 text-teal-600"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Icon className="h-5 w-5" />
                        <span className="text-sm font-medium">
                          {item.label}
                        </span>
                      </div>
                      {item.external && (
                        <ExternalLink className="h-4 w-4 text-gray-400" />
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>

            {/* ACCOUNT Section */}
            <div className="mb-6 px-2">
              <div className="mb-2 px-3">
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                  ACCOUNT
                </span>
              </div>
              <div className="space-y-1">
                {accountItems.map(item => {
                  const Icon = item.icon;
                  const active = isActive(item.path);
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center gap-3 rounded-lg px-3 py-2.5 transition-colors ${
                        active
                          ? "bg-gray-100 text-teal-600"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      <Icon className="h-5 w-5" />
                      <span className="text-sm font-medium">{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          </nav>

          {/* Bottom Section */}
          <div className="space-y-2 border-t border-gray-200 p-4">
            <Link
              to="/nps-user/help"
              className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-gray-700 transition-colors hover:bg-gray-50"
            >
              <HelpCircle className="h-5 w-5" />
              <span className="text-sm font-medium">Help & Support</span>
            </Link>
            <button className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-gray-700 transition-colors hover:bg-gray-50">
              <LogOut className="h-5 w-5" />
              <span className="text-sm font-medium">Logout</span>
            </button>
          </div>
        </aside>

        {/* Main Content Area */}
        <div className="flex flex-1 flex-col overflow-hidden">
          {/* Header */}
          <header className="flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
            {/* Search Bar */}
            <div className="mx-4 max-w-2xl flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 transform text-gray-400" />
                <input
                  type="text"
                  placeholder="Search investments, transactions..."
                  className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
            </div>

            {/* Right Side - Notifications, Avatar, User Info */}
            <div className="flex items-center gap-4">
              {/* Notification Bell */}
              <button className="relative rounded-lg p-2 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-800">
                <Bell className="h-5 w-5" />
                <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-red-500" />
              </button>

              {/* User Avatar and Info */}
              <UserInfo />
            </div>
          </header>

          {/* Main Content */}
          <main className="flex-1 overflow-y-auto bg-gray-50">
            <Outlet />
          </main>
        </div>
      </div>
    </RegistrationDetailsProvider>
  );
};

export default NpsUserLayout;
